<template>
  <form
    :id="props.id"
    ref="formRef"
    :class="DS_FORM_CLASSES.Root"
    :action="props.action"
    :method="props.method"
    :data-turbo="props.turbo"
    :data-turbo-frame="props.turboFrame"
    :data-turbo-action="props.turboAction"
    :enctype="props.enctype"
    data-cy="ds-form"
    novalidate
    @submit="handleNativeSubmit"
  >
    <div
      v-if="formStore.formErrorMessages.value.length > 0"
      :class="DS_FORM_CLASSES.Errors"
      data-cy="ds-form__errors"
      data-ds-error
    >
      <DSAlert
        v-for="(errorMessage, index) in formStore.formErrorMessages.value"
        :key="`${errorMessage}-${index}`"
        variant="error"
        :body="errorMessage"
        @dismiss="removeFormErrorMessage(index)"
      />
    </div>

    <slot />
  </form>
</template>

<script setup lang="ts">
import { getCurrentInstance, onBeforeUnmount, onMounted, provide, ref } from "vue";
import {
  DS_FORM_CUSTOM_ELEMENT_SELECTOR,
  DS_FORM_STORE_DOM_KEY,
  DS_FORM_STORE_PROVIDE_KEY,
} from "../constants/provide";
import { useDSFormStore } from "../store/dsFormStore";
import type { DSFormEmits } from "../types/emits";
import type { DSFormProps } from "../types/props";
import { DS_FORM_CLASSES } from "@/src/design_system/form_elements/form/constants/classes";
import DSAlert from "@/src/design_system/feedback/alert/components/DSAlert.vue";
import { EasyErrorFragment } from "@/src/shared/graphql/fragments/error.generated";

const props = withDefaults(defineProps<DSFormProps>(), {
  action: undefined,
  id: undefined,
  method: undefined,
  turbo: undefined,
  turboFrame: undefined,
  turboAction: undefined,
  initialErrors: undefined,
  enctype: undefined,
});
const emit = defineEmits<DSFormEmits>();

const formRef = ref<HTMLFormElement>();
const formStore = useDSFormStore();

provide(DS_FORM_STORE_PROVIDE_KEY, formStore);

const getHostCustomElement = (): HTMLElement | null =>
  getCurrentInstance()?.vnode.el?.closest?.(DS_FORM_CUSTOM_ELEMENT_SELECTOR) ?? null;

onMounted(async () => {
  const host = getHostCustomElement();
  if (host) {
    (host as unknown as Record<string, unknown>)[DS_FORM_STORE_DOM_KEY] = formStore;
  }

  if (props.initialErrors?.length) {
    setTimeout(() => {
      formStore.setEasyErrors(props.initialErrors!, formRef.value);
    }, 0);
  }
});

onBeforeUnmount(() => {
  const host = getHostCustomElement();
  if (host) {
    delete (host as unknown as Record<string, unknown>)[DS_FORM_STORE_DOM_KEY];
  }
});

const handleNativeSubmit = (e: Event) => {
  if (!props.action) e.stopPropagation();

  const submitEvent = e as SubmitEvent;
  const submitter = "submitter" in submitEvent ? (submitEvent.submitter as HTMLButtonElement | null) : null;
  const actionId = getSubmitterActionId(submitter);

  formStore.resetValidation();
  const hasErrors = formStore.validate();

  if (hasErrors) {
    e.preventDefault();
    return;
  }

  if (props.action) {
    return;
  }

  e.preventDefault();
  emit("submit", actionId);
};

const getSubmitterActionId = (submitter: HTMLButtonElement | null): string | null => {
  return submitter?.name === "action" ? submitter.value : null;
};

const removeFormErrorMessage = (indexOfMessage: number) => {
  formStore.removeFormErrorMessage(indexOfMessage);
};

defineExpose({
  resetValidation: () => formStore.resetValidation(),
  setEasyErrors: (errors: EasyErrorFragment[]) => formStore.setEasyErrors(errors, formRef.value),
  validate: () => formStore.validate(),
});
</script>
